import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { VoucherService, PublicVoucherVerification } from '../../services/voucher.service';
import { CompanyGroupService, RedemptionLocation } from '../../services/company-group.service';
import { LogoComponent } from '../logo/logo.component';
import { LoggerService } from '../../services/logger.service';
import { environment } from '../../../environments/environment';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideStore, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { VoucherBalanceCardComponent } from '../voucher-balance-card/voucher-balance-card.component';
import {
  RedemptionFlowComponent,
  RedemptionResult,
} from '../redemption-flow/redemption-flow.component';
import { VoucherTimelineComponent } from '../voucher-timeline/voucher-timeline.component';
import { setActiveCurrency } from '../../utils/currency.utils';
import { CompanyService } from '../../services/company.service';

@Component({
  selector: 'app-voucher-verification',
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmItemImports,
    HlmNativeSelectImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    LogoComponent,
    VoucherBalanceCardComponent,
    RedemptionFlowComponent,
    VoucherTimelineComponent,
  ],
  providers: [provideIcons({ lucideStore, lucideTriangleAlert })],
  // CompanyGroupService injected but not imported as module (it's a service)
  templateUrl: './voucher-verification.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./voucher-verification.component.css'],
})
export class VoucherVerificationComponent implements OnInit, OnDestroy {
  voucher: PublicVoucherVerification | null = null;
  loading: boolean = true;
  error: string = '';
  verificationKey: string = '';

  // Redemption
  redemptionPin: string = '';
  redeeming: boolean = false;
  redemptionResult: RedemptionResult | null = null;
  redemptionError: string = '';

  // Franchise / group members & redemption locations (single source)
  redemptionLocations: RedemptionLocation[] = [];
  selectedMemberCompanyId: string | null = null;
  loadingMembers: boolean = false;

  /** The page is public: amounts follow the voucher's currency. Leaving it, they
   * follow the logged-in company's again (euro without one). */
  private readonly companyService = inject(CompanyService);

  constructor(
    private route: ActivatedRoute,
    private voucherService: VoucherService,
    private groupService: CompanyGroupService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.verificationKey = params['voucherCode']; // Keep same route param name for compatibility
      if (this.verificationKey) {
        this.loadVoucherInfo();
      } else {
        this.error = 'Ungültiger Gutschein-Verifizierungsschlüssel';
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    const company = this.companyService.company();
    setActiveCurrency(company?.currency, company?.currency_locale);
  }

  private showVoucher(voucher: PublicVoucherVerification) {
    setActiveCurrency(voucher.currency, voucher.currency_locale);
    this.voucher = voucher;
  }

  loadVoucherInfo() {
    this.loading = true;
    this.error = '';

    this.voucherService.verifyVoucherPublic(this.verificationKey).subscribe({
      next: (voucher) => {
        this.showVoucher(voucher);
        this.loading = false;
        if (voucher.public_company_id) {
          this.loadRedemptionLocations(voucher.public_company_id);
        }
      },
      error: (error) => {
        this.logger.error('Error loading voucher:', error);
        if (error.status === 404) {
          this.error = 'Gutschein nicht gefunden';
        } else {
          this.error = 'Fehler beim Laden des Gutscheins';
        }
        this.loading = false;
      },
    });
  }

  /** New balance and history after a redemption, without the loading skeleton. */
  private refreshVoucher() {
    this.voucherService.verifyVoucherPublic(this.verificationKey).subscribe({
      next: (voucher) => this.showVoucher(voucher),
      error: (error) => this.logger.error('Error refreshing voucher:', error),
    });
  }

  loadRedemptionLocations(companyId: string): void {
    this.loadingMembers = true;
    this.groupService.getRedemptionLocations(companyId).subscribe({
      next: (locations) => {
        this.redemptionLocations = locations;
        this.loadingMembers = false;
        this.preselectRememberedLocation(companyId);
      },
      error: () => {
        this.redemptionLocations = [];
        this.loadingMembers = false;
      },
    });
  }

  /**
   * The location this device redeemed at last time for this network. A wrong pick
   * costs the picked location a PIN attempt (three lock it), so staff choose once per
   * device. Stored only in this browser; storage may be unavailable (private mode).
   */
  private locationKey(companyId: string): string {
    return `verify_location:${companyId}`;
  }

  private preselectRememberedLocation(companyId: string): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(this.locationKey(companyId));
    } catch {
      return;
    }
    // Only members: a network's Zentrale doesn't redeem, even if this device once did.
    const known = this.membersWithPin.some((m) => m.company_id === stored);
    if (stored && known) this.selectedMemberCompanyId = stored;
  }

  private rememberLocation(): void {
    const companyId = this.voucher?.public_company_id;
    if (!companyId || !this.selectedMemberCompanyId) return;
    try {
      localStorage.setItem(this.locationKey(companyId), this.selectedMemberCompanyId);
    } catch {
      /* storage unavailable: the choice just isn't remembered */
    }
  }

  /**
   * The network members among the locations. A shop's own further locations carry
   * its own company_id: they are shown, but redeeming there is redeeming at the shop.
   */
  get networkMembers(): RedemptionLocation[] {
    const own = this.voucher?.public_company_id;
    return this.redemptionLocations.filter((l) => l.company_id !== own);
  }

  get membersWithPin(): RedemptionLocation[] {
    return this.networkMembers.filter((l) => l.has_pin);
  }

  getLogoUrl(path: string): string {
    const clean = path.startsWith('uploads/') ? path.substring(8) : path;
    return `${environment.apiUrl}/images/${clean}`;
  }

  /** A network voucher: the location has to be picked, PIN or not (see the dropdown). */
  get hasGroupMembers(): boolean {
    return this.networkMembers.length > 0;
  }

  /** Shown in the review step, so staff see where the PIN will be checked. */
  get selectedLocationName(): string | null {
    return (
      this.networkMembers.find((l) => l.company_id === this.selectedMemberCompanyId)
        ?.company_name ?? null
    );
  }

  get redemptionReady(): boolean {
    return (
      this.redemptionPin.length >= 4 &&
      !this.loadingMembers &&
      (!this.hasGroupMembers || !!this.selectedMemberCompanyId)
    );
  }

  /** Kept on screen after the last euro is redeemed, so the confirmation stays visible. */
  get showRedemption(): boolean {
    return (
      !!this.redemptionResult || (this.voucher?.status === 'active' && this.voucher.amount > 0)
    );
  }

  goHome() {
    window.location.href = 'https://www.schenkwerk.com';
  }

  redeemVoucher(amountInCents: number | null) {
    if (!this.redemptionReady || this.redeeming || !this.voucher) return;

    const balance = this.voucher.amount;
    this.redeeming = true;
    this.redemptionError = '';
    this.redemptionResult = null;

    this.voucherService
      .redeemVoucherPublic(
        this.verificationKey,
        this.redemptionPin,
        amountInCents ?? undefined,
        this.selectedMemberCompanyId || undefined,
      )
      .subscribe({
        next: (response) => {
          this.redemptionResult = {
            redeemed: response.redeemed_amount ?? amountInCents ?? balance,
            remaining: response.remaining_amount,
          };
          this.redeeming = false;
          this.rememberLocation();
          this.redemptionPin = '';
          this.refreshVoucher();
        },
        error: (error) => {
          this.logger.error('Error redeeming voucher:', error);
          if (error.status === 403) {
            this.redemptionError =
              error.error?.detail ||
              'Ungültiger PIN oder öffentliche Einlösung ist nicht aktiviert';
          } else if (error.status === 404) {
            this.redemptionError = 'Gutschein nicht gefunden';
          } else if (error.status === 400) {
            this.redemptionError = error.error?.detail || 'Fehler bei der Einlösung';
          } else {
            this.redemptionError = 'Fehler beim Einlösen des Gutscheins';
          }
          this.redeeming = false;
        },
      });
  }

  /** The backend locks public redemption after three wrong PINs ("… gesperrt"). */
  isBlocked(): boolean {
    return this.redemptionError.includes('gesperrt') && !this.redemptionError.includes('Noch');
  }

  getErrorTitle(): string {
    if (this.isBlocked()) {
      return 'Einlösung gesperrt';
    }
    if (this.redemptionError.startsWith('Ungültiger PIN')) {
      return 'Falscher PIN';
    }
    return 'Fehler bei der Einlösung';
  }

  continueRedemption() {
    this.redemptionResult = null;
    this.redemptionError = '';
  }
}
